import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Trophy, ChevronRight, Medal, Target, Calendar, Clock, Filter, ChevronDown } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { currentSeason } from "../utils/seasons";

const CupCard = ({ cup }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`cup-${cup.id}`}>
    <div className="p-6 border-b border-slate-100">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-14 h-14 rounded-lg bg-[#7C2C3B] flex items-center justify-center">
          <Trophy className="w-7 h-7 text-[#F8D742]" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
            {cup.competition_name.toUpperCase()}
          </h3>
          <p className="text-sm text-slate-500">{cup.season}</p>
        </div>
      </div>
      
      {cup.final_position && (
        <div className="inline-flex items-center gap-2 bg-[#F8D742]/20 text-[#7C2C3B] px-3 py-1 rounded-full text-sm font-semibold">
          <Medal className="w-4 h-4" />
          {cup.final_position}
        </div>
      )}
    </div>

    <div className="p-6">
      <p className="text-slate-600 text-sm leading-relaxed mb-6">{cup.summary}</p>
      
      {cup.matches && cup.matches.length > 0 && (
        <Accordion type="single" collapsible>
          <AccordionItem value="matches">
            <AccordionTrigger className="text-sm font-semibold text-[#7C2C3B]">
              View Matches ({cup.matches.length})
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 pt-3">
                {cup.matches.map((match, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-500 uppercase">{match.round}</span>
                      <span className="text-xs text-slate-400">{match.date}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-[#0F172A]">vs {match.opponent}</span>
                      <span className="text-lg font-bold text-[#7C2C3B]" style={{ fontFamily: 'Barlow Condensed' }}>
                        {match.score}
                      </span>
                    </div>
                    {match.summary && (
                      <p className="text-xs text-slate-500 mt-2">{match.summary}</p>
                    )}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  </div>
);

export default function CupsPage() {
  const [cups, setCups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDecade, setSelectedDecade] = useState("all");
  const [showArchive, setShowArchive] = useState(false);

  useEffect(() => {
    fetchCups();
  }, []);

  const fetchCups = async () => {
    try {
      const response = await axios.get(`${API}/cups`);
      setCups(response.data);
    } catch (error) {
      console.error("Failed to fetch cups:", error);
    } finally {
      setLoading(false);
    }
  };

  // Separate current season from historical
  const activeSeason = currentSeason();
  const currentSeasonCups = cups.filter(cup => cup.season === activeSeason);
  const historicalCups = cups.filter(cup => cup.season !== activeSeason);

  // Get decade from season (e.g., "1964-65" -> "1960s")
  const getDecade = (season) => {
    const year = parseInt(season?.split("-")[0]);
    if (isNaN(year)) return "Unknown";
    const decadeStart = Math.floor(year / 10) * 10;
    return `${decadeStart}s`;
  };

  // Get available decades
  const decades = [...new Set(historicalCups.map(cup => getDecade(cup.season)))].sort().reverse();

  // Filter historical cups by decade
  const filteredHistoricalCups = selectedDecade === "all" 
    ? historicalCups 
    : historicalCups.filter(cup => getDecade(cup.season) === selectedDecade);

  // Group cups by competition
  const groupCupsByCompetition = (cupsList) => {
    return cupsList.reduce((acc, cup) => {
      const comp = cup.competition_name;
      if (!acc[comp]) acc[comp] = [];
      acc[comp].push(cup);
      return acc;
    }, {});
  };

  const currentGrouped = groupCupsByCompetition(currentSeasonCups);
  const historicalGrouped = groupCupsByCompetition(filteredHistoricalCups);

  // Count trophies won
  const trophiesWon = cups.filter(c => 
    c.final_position?.toLowerCase().includes('winner') || 
    c.final_position?.toLowerCase().includes('champions')
  ).length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cups-page">
      <SEO title="Cup Competitions" path="/cups" description="West Ham United in cup competitions. FA Cup, EFL Cup, European trophies and historic cup runs from the Hammers through the decades." />
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#7C2C3B] py-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,#F8D742_0%,transparent_70%)]"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            CUP COMPETITIONS
          </h1>
          <p className="text-white/80">West Ham United's tournament history</p>
        </div>
      </div>

      {/* Trophy Stats */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#F8D742] mx-auto mb-3 flex items-center justify-center">
                <Trophy className="w-8 h-8 text-[#7C2C3B]" />
              </div>
              <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>3</div>
              <div className="text-sm text-slate-500">FA Cups</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#2BAEE5] mx-auto mb-3 flex items-center justify-center">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>2</div>
              <div className="text-sm text-slate-500">European Trophies</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 mx-auto mb-3 flex items-center justify-center">
                <Medal className="w-8 h-8 text-[#7C2C3B]" />
              </div>
              <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>2</div>
              <div className="text-sm text-slate-500">FA Cup Finals</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 mx-auto mb-3 flex items-center justify-center">
                <Target className="w-8 h-8 text-[#7C2C3B]" />
              </div>
              <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>1965</div>
              <div className="text-sm text-slate-500">Cup Winners' Cup</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : cups.length === 0 ? (
          <div className="empty-state">
            <Trophy className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>NO CUP DATA YET</h3>
            <p className="text-slate-500">Admin can add cup competition data via the admin panel.</p>
          </div>
        ) : (
          <Tabs defaultValue="current" className="space-y-6">
            <TabsList className="grid w-full max-w-md grid-cols-2 mx-auto">
              <TabsTrigger value="current" className="flex items-center gap-2 data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white">
                <Trophy className="w-4 h-4" />
                {activeSeason} Season
              </TabsTrigger>
              <TabsTrigger value="archive" className="flex items-center gap-2 data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white">
                <Clock className="w-4 h-4" />
                Cup History
              </TabsTrigger>
            </TabsList>

            {/* Current Season Tab */}
            <TabsContent value="current">
              {currentSeasonCups.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                  <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No current season cup data available</p>
                </div>
              ) : (
                <div className="space-y-12">
                  {Object.entries(currentGrouped).map(([competition, competitionCups]) => (
                    <div key={competition}>
                      <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
                        {competition.toUpperCase()}
                      </h2>
                      <div className="grid md:grid-cols-2 gap-6">
                        {competitionCups.map(cup => (
                          <CupCard key={cup.id} cup={cup} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Historical Archive Tab */}
            <TabsContent value="archive">
              {/* Decade Filter */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
                    <Filter className="w-4 h-4" />
                    Filter by Era:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedDecade("all")}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        selectedDecade === "all" 
                          ? "bg-[#7C2C3B] text-white" 
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      All Eras ({historicalCups.length})
                    </button>
                    {decades.map(decade => {
                      const count = historicalCups.filter(c => getDecade(c.season) === decade).length;
                      return (
                        <button
                          key={decade}
                          onClick={() => setSelectedDecade(decade)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                            selectedDecade === decade 
                              ? "bg-[#7C2C3B] text-white" 
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {decade} ({count})
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Historical Cups */}
              {filteredHistoricalCups.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No historical cup data for this era</p>
                </div>
              ) : (
                <div className="space-y-12">
                  {Object.entries(historicalGrouped).map(([competition, competitionCups]) => (
                    <div key={competition}>
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                          {competition.toUpperCase()}
                        </h2>
                        <span className="text-sm text-slate-500">
                          {competitionCups.length} season{competitionCups.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        {competitionCups.sort((a, b) => {
                          const yearA = parseInt(a.season?.split("-")[0]) || 0;
                          const yearB = parseInt(b.season?.split("-")[0]) || 0;
                          return yearB - yearA;
                        }).map(cup => (
                          <CupCard key={cup.id} cup={cup} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Summary Stats for Archive */}
              <div className="mt-8 bg-gradient-to-r from-[#7C2C3B] to-[#5a1f2b] rounded-xl p-6 text-white">
                <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                  HISTORICAL CUP SUMMARY
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-3xl font-bold text-[#F8D742]">{historicalCups.length}</div>
                    <div className="text-sm text-white/70">Cup Campaigns</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#F8D742]">{trophiesWon}</div>
                    <div className="text-sm text-white/70">Trophies Won</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#F8D742]">
                      {historicalCups.reduce((sum, c) => sum + (c.matches?.length || 0), 0)}
                    </div>
                    <div className="text-sm text-white/70">Total Matches</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#F8D742]">{decades.length}</div>
                    <div className="text-sm text-white/70">Decades Covered</div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Footer />
    </div>
  );
}
